import React, { useState, useEffect } from "react";
import {
  Dialog,
  Drawer,
  Typography,
  Button,
  Box,
  IconButton,
  Chip,
  Stack,
  useMediaQuery,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
} from "@mui/material";import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import { useTheme } from "@mui/material/styles";
import { IMAGE_URL } from "../../services/api";
import ProtectedImage from "../../components/ProtectedImage";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";
import { useDispatch } from "react-redux";
import TaskVeiwController from "../../redux/actions/TaskApiAction";

const priorityColors = {
  Low: "#8BC34A",
  Medium: "#FFC107",
  High: "#FF5722",
  Critical: "#F44336",
};

const statusColors = {
  New: "#2196F3",
  Pending: "#FF9800",
  "In Progress": "#9C27B0",
  "Ready for Testing": "#00BCD4",
  Testing: "#3F51B5",
  "Changes Required": "#FF5722",
  Reopened: "#FFC107",
  Completed: "#4CAF50",
  Blocked: "#F44336",
};

const FieldLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      color: BRAND.muted,
      fontWeight: 600,
      fontSize: "0.6875rem",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getAssigneeName = (task) => {
  let name = "—";
  if (task?.assigned_to?.name) {
    name = task.assigned_to.name;
  } else if (task?.assigned_to?.first_name || task?.assigned_to?.last_name) {
    const first = task.assigned_to.first_name || "";
    const last = task.assigned_to.last_name || "";
    name = `${first} ${last}`.trim();
  }

  const empId = task?.assigned_to?.employee_id;
  if (empId && name !== "—") {
    return `${name} (${empId})`;
  }
  return name;
};

const TaskFields = ({ selectedTask, onViewDocument }) => (
  <Stack spacing={2}>

    <Box>
      <FieldLabel>Title</FieldLabel>
      <Typography
        sx={{
          color: BRAND.navy,
          fontWeight: 600,
          fontSize: "1.0625rem",
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}
      >
        {selectedTask.title}
      </Typography>
    </Box>

    <Box>
      <FieldLabel>Description</FieldLabel>
      <Typography
        sx={{
          color: BRAND.text,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          fontSize: "0.9375rem",
        }}
      >
        {selectedTask.description || "—"}
      </Typography>
    </Box>

    <Box>
      <FieldLabel>Assigned To</FieldLabel>
      <Typography sx={{ color: BRAND.navy, fontWeight: 500, fontSize: "0.9375rem" }}>
        {getAssigneeName(selectedTask)}
      </Typography>
    </Box>

    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <FieldLabel>Priority</FieldLabel>
        <Chip
          label={selectedTask.priority || "—"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.8125rem",
            height: 28,
            borderRadius: "8px",
            bgcolor: `${priorityColors[selectedTask.priority] || BRAND.muted}22`,
            color: priorityColors[selectedTask.priority] || BRAND.muted,
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <FieldLabel>Status</FieldLabel>
        <Chip
          label={selectedTask.status || "—"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.8125rem",
            height: 28,
            borderRadius: "8px",
            bgcolor: `${statusColors[selectedTask.status] || BRAND.muted}22`,
            color: statusColors[selectedTask.status] || BRAND.muted,
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </Box>
    </Box>

    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <FieldLabel>Start Date</FieldLabel>
        <Typography sx={{ color: BRAND.navy, fontWeight: 500, fontSize: "0.9375rem" }}>
          {formatDate(selectedTask.start_date)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <FieldLabel>End Date</FieldLabel>
        <Typography sx={{ color: BRAND.navy, fontWeight: 500, fontSize: "0.9375rem" }}>
          {formatDate(selectedTask.end_date)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <FieldLabel>Created At</FieldLabel>
        <Typography sx={{ color: BRAND.navy, fontWeight: 500, fontSize: "0.9375rem" }}>
          {formatDate(selectedTask.created_at)}
        </Typography>
      </Box>
    </Box>

    {selectedTask.attachments?.length > 0 && (
      <Box>
        <FieldLabel>Attachments</FieldLabel>
        <Stack spacing={1}>
          {selectedTask.attachments.map((url, index) => {
            const fullUrl = `${IMAGE_URL}${url}`;
            const isPDF = fullUrl.toLowerCase().endsWith(".pdf");
            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fullUrl);

            return (
              <Box key={index}>
                {isImage ? (
                  <ProtectedImage
                    path={fullUrl}
                    alt={`Attachment ${index + 1}`}
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                    onClick={() => onViewDocument(fullUrl, false)}
                  />
                ) : isPDF ? (
                  <Typography
                    sx={{
                      color: BRAND.orange,
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                    }}
                    onClick={() => onViewDocument(fullUrl, true)}
                  >
                    View PDF {index + 1}
                  </Typography>
                ) : (
                  <Typography
                    component="a"
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: BRAND.orange, fontSize: "0.875rem" }}
                  >
                    Download File {index + 1}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    )}
  </Stack>
);

const StatusReasonAlert = ({ reason }) => {
  if (!reason) return null;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 2,
        mb: 3,
        bgcolor: "#FFF4E5",
        border: "1px solid #FFE0B2",
        borderRadius: "8px",
        alignItems: "flex-start",
      }}
    >
      <WarningOutlinedIcon sx={{ color: "#ED6C02", fontSize: "1.25rem", mt: 0.25 }} />
      <Box>
        <Typography sx={{ color: "#b75b00", fontWeight: 700, fontSize: "0.875rem", mb: 0.5 }}>
          Status Note
        </Typography>
        <Typography sx={{ color: "#E65100", fontSize: "0.875rem", lineHeight: 1.5 }}>
          {reason}
        </Typography>
      </Box>
    </Box>
  );
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DocumentViewer = ({ url, open, onClose, isPdf }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", m: 2, bgcolor: "#fff", display: "flex", flexDirection: "column" }
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, borderBottom: `1px solid ${BRAND.border}` }}>
        <Typography sx={{ fontWeight: 600, color: BRAND.navy }}>Document Viewer</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden", p: 0, bgcolor: "#f3f4f6" }}>
        {isPdf ? (
          <iframe src={url} width="100%" height="100%" style={{ border: "none" }} title="PDF Viewer" />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={url} alt="Attachment" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

const getLogBadge = (action) => {
  const lowerAction = action.toLowerCase();
  if (lowerAction.includes("created")) return { label: "Created", color: "#4CAF50", bgcolor: "#E8F5E9" };
  if (lowerAction.includes("status changed")) return { label: "Status", color: "#FF9800", bgcolor: "#FFF3E0" };
  if (lowerAction.includes("priority changed")) return { label: "Priority", color: "#F44336", bgcolor: "#FFEBEE" };
  if (lowerAction.includes("attachment")) return { label: "Attachment", color: "#9C27B0", bgcolor: "#F3E5F5" };
  if (lowerAction.includes("assigned")) return { label: "Assigned", color: "#2196F3", bgcolor: "#E3F2FD" };
  return { label: "Updated", color: "#00BCD4", bgcolor: "#E0F7FA" };
};

const TaskHistory = ({ logs }) => {
  if (!logs || logs.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center", color: BRAND.muted }}>
        <Typography>No activity logs yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      <Stack spacing={2}>
            {logs.map((log) => {
              const badge = getLogBadge(log.action);
              return (
                <Box key={log.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", width: "100%" }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: BRAND.orangeLight || "#FFF4E5",
                      color: BRAND.orange,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      flexShrink: 0,
                    }}
                  >
                    {log.performed_by_name?.charAt(0)?.toUpperCase() || "S"}
                  </Box>
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                    <Box>
                      <Typography sx={{ color: BRAND.navy, fontWeight: 600, fontSize: "0.875rem" }}>
                        {log.performed_by_name}
                      </Typography>
                      <Typography sx={{ color: "#4B5563", fontSize: "0.875rem", mt: 0.25 }}>
                        {log.action}
                      </Typography>
                      <Typography sx={{ color: BRAND.muted, fontSize: "0.75rem", mt: 0.5 }}>
                        {formatDateTime(log.timestamp)}
                      </Typography>
                    </Box>
                    <Chip
                      label={badge.label}
                      size="small"
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 700,
                        height: 20,
                        bgcolor: badge.bgcolor,
                        color: badge.color,
                        border: `1px solid ${badge.color}40`,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
      </Stack>
    </Box>
  );
};

const TaskComments = ({ comments, taskId, category, project, is_deleted }) => {
  const dispatch = useDispatch();
  const { addTaskComment } = TaskVeiwController();
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    try {
      await dispatch(addTaskComment(taskId, commentText, category, project));
      setCommentText("");
    } catch (error) {
      // Error handled by action
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflowY: "auto", pb: 2 }}>
        {!comments || comments.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: BRAND.muted }}>
            <Typography>No comments yet. Be the first to comment!</Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {comments.map((c) => (
              <Box key={c.id} sx={{ display: "flex", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: `${BRAND.orange}22`,
                    color: BRAND.orange,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    flexShrink: 0,
                  }}
                >
                  {c.user_name?.charAt(0)?.toUpperCase() || "U"}
                </Box>
                <Box sx={{ flex: 1, bgcolor: "#F3F4F6", p: 1.5, borderRadius: "0 12px 12px 12px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                    <Typography sx={{ color: BRAND.navy, fontWeight: 600, fontSize: "0.875rem" }}>
                      {c.user_name}
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: "0.75rem" }}>
                      {formatDateTime(c.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#374151", fontSize: "0.875rem", whiteSpace: "pre-wrap" }}>
                    {c.comment}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      {!is_deleted && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: `1px solid ${BRAND.border}`,
            display: "flex",
            gap: 1,
            alignItems: "stretch",
          }}
        >
        <TextField
          size="small"
          fullWidth
          placeholder="Write a comment..."
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#F9FAFB",
              height: "40px",
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!commentText.trim() || isSubmitting}
          sx={{
            ...primaryButtonSx,
            borderRadius: "12px",
            minWidth: 80,
            height: "40px",
          }}
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Post"}
        </Button>
        </Box>
      )}
    </Box>
  );
};

const TaskView = ({ show, handleClose, selectedTask, category = "Task" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabIndex, setTabIndex] = useState(0);
  const [viewerState, setViewerState] = useState({ open: false, url: null, isPdf: false });

  const handleViewDocument = (url, isPdf) => {
    setViewerState({ open: true, url, isPdf });
  };

  useEffect(() => {
    if (show) setTabIndex(0);
  }, [show, selectedTask?.id]);

  if (!selectedTask) return null;

  const title =
    category?.toLowerCase() === "issue" || selectedTask.category === "Issue"
      ? "Issue Details"
      : "Task Details";

  if (isMobile) {
    return (
      <Dialog
        open={show}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: BRAND.bg,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            maxHeight: "100dvh",
            borderRadius: 0,
            m: 0,
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            flexShrink: 0,
            borderBottom: `1px solid ${BRAND.border}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 1,
              px: 0.5,
              pr: 2,
            }}
          >
            <IconButton
              sx={{ color: BRAND.navy }}
              onClick={handleClose}
              aria-label="Close task view"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontSize: "1.0625rem",
                  fontWeight: 700,
                  color: BRAND.navy,
                  lineHeight: 1.25,
                }}
              >
                {title}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                {selectedTask.project?.title && (
                  <Chip
                    label={selectedTask.project.title}
                    size="small"
                    sx={{ fontSize: "0.6875rem", height: 20, bgcolor: `${BRAND.orange}22`, color: BRAND.orange, fontWeight: 600 }}
                  />
                )}
                {selectedTask.category && (
                  <Chip
                    label={selectedTask.category}
                    size="small"
                    sx={{ fontSize: "0.6875rem", height: 20, bgcolor: `${BRAND.navy}22`, color: BRAND.navy, fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Tabs
            value={tabIndex}
            onChange={(e, newVal) => setTabIndex(newVal)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "#fff",
              "& .MuiTab-root": { fontWeight: 600 },
              "& .Mui-selected": { color: BRAND.orange },
              "& .MuiTabs-indicator": { backgroundColor: BRAND.orange },
            }}
          >
            <Tab label="Details" />
            <Tab label="Logs" />
            <Tab label="Comments" />
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: 2,
            pt: 2,
            pb: 2,
            bgcolor: BRAND.bg,
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              border: `1px solid ${BRAND.border}`,
              p: 2,
              minHeight: 200,
            }}
          >
            {tabIndex === 0 && (
              <>
                <StatusReasonAlert reason={selectedTask.status_reason} />
                <TaskFields selectedTask={selectedTask} onViewDocument={handleViewDocument} />
              </>
            )}
            {tabIndex === 1 && <TaskHistory logs={selectedTask.logs} />}
            {tabIndex === 2 && (
              <TaskComments
                comments={selectedTask.comments}
                taskId={selectedTask.id}
                category={category}
                project={selectedTask.project?.id}
              />
            )}
          </Box>
        </Box>
        {viewerState.open && (
          <DocumentViewer
            open={viewerState.open}
            onClose={() => setViewerState({ open: false, url: null, isPdf: false })}
            url={viewerState.url}
            isPdf={viewerState.isPdf}
          />
        )}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 450, md: 500 },
          bgcolor: BRAND.bg,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          bgcolor: "#fff",
          borderBottom: `1px solid ${BRAND.border}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ color: BRAND.navy, fontWeight: 700, fontSize: "1.25rem" }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
            {selectedTask.project?.title && (
              <Chip
                label={selectedTask.project.title}
                size="small"
                sx={{ fontSize: "0.75rem", height: 24, bgcolor: `${BRAND.orange}22`, color: BRAND.orange, fontWeight: 600 }}
              />
            )}
            {selectedTask.category && (
              <Chip
                label={selectedTask.category}
                size="small"
                sx={{ fontSize: "0.75rem", height: 24, bgcolor: `${BRAND.navy}22`, color: BRAND.navy, fontWeight: 600 }}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={tabIndex}
        onChange={(e, newVal) => setTabIndex(newVal)}
        variant="fullWidth"
        sx={{
          borderBottom: `1px solid ${BRAND.border}`,
          bgcolor: "#fff",
          "& .MuiTab-root": { fontWeight: 600, py: 2 },
          "& .Mui-selected": { color: BRAND.orange },
          "& .MuiTabs-indicator": { backgroundColor: BRAND.orange },
        }}
      >
        <Tab label="Details" />
        <Tab label="Logs" />
        <Tab label="Comments" />
      </Tabs>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, bgcolor: "#fff" }}>
        {tabIndex === 0 && (
          <>
            <StatusReasonAlert reason={selectedTask.status_reason} />
            <TaskFields selectedTask={selectedTask} onViewDocument={handleViewDocument} />
          </>
        )}
        {tabIndex === 1 && <TaskHistory logs={selectedTask.logs} />}
        {tabIndex === 2 && (
          <TaskComments
            comments={selectedTask.comments}
            taskId={selectedTask.id}
            category={category}
            project={selectedTask.project?.id}
            is_deleted={selectedTask.is_deleted}
          />
        )}
      </Box>
      {viewerState.open && (
        <DocumentViewer
          open={viewerState.open}
          onClose={() => setViewerState({ open: false, url: null, isPdf: false })}
          url={viewerState.url}
          isPdf={viewerState.isPdf}
        />
      )}
    </Drawer>
  );
};

export default TaskView;
